/**
 * Съёмочные площадки: типы ответа `GET /api/library/{id}/sites` и чистые помощники экрана.
 * Без runtime-импортов — файл грузится напрямую `node --test`.
 */
import type { ReferenceItem } from "./library.ts";

export type SiteLevel = "strict" | "normal" | "wide";

export const LEVEL_LABEL: Record<SiteLevel, string> = { strict: "строже", normal: "обычно", wide: "шире" };
export const LEVELS: readonly SiteLevel[] = ["strict", "normal", "wide"];

export type Site = {
  number: number;
  size: number;
  dresses: number;
  cover_id: number | null;
  item_ids: number[];
};

export type SitesData = {
  level: SiteLevel;
  levels: SiteLevel[];
  sites: Site[];
  closeups: number;
  missing: number;
  total: number;
};

/** Уровень из адреса (`site_level`); нет или мусор — `normal`. */
export function siteLevel(params: { get(name: string): string | null }): SiteLevel {
  const v = params.get("site_level");
  return v === "strict" || v === "wide" ? v : "normal";
}

/** Фото площадки в её порядке; id, которых нет среди `byId` (удалены, отфильтрованы), пропускаются. */
export function siteItems(site: Pick<Site, "item_ids">, byId: ReadonlyMap<number, ReferenceItem>): ReferenceItem[] {
  const out: ReferenceItem[] = [];
  for (const id of site.item_ids) {
    const item = byId.get(id);
    if (item) out.push(item);
  }
  return out;
}

/** Площадки с найденными среди `byId` фото (пустые отбрасываются): число фото плитки — по найденным, не по ответу сервера. */
export function foundSites(
  sites: readonly Site[],
  byId: ReadonlyMap<number, ReferenceItem>,
): { site: Site; found: ReferenceItem[] }[] {
  const out: { site: Site; found: ReferenceItem[] }[] = [];
  for (const site of sites) {
    const found = siteItems(site, byId);
    if (found.length > 0) out.push({ site, found });
  }
  return out;
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** «N площадок · K крупных планов · M без расчёта»; нулевые части опускаются. */
export function sitesCaption(data: Pick<SitesData, "sites" | "closeups" | "missing">): string {
  const parts: string[] = [];
  const n = data.sites.length;
  if (n > 0) parts.push(`${n} ${plural(n, "площадка", "площадки", "площадок")}`);
  if (data.closeups > 0)
    parts.push(`${data.closeups} ${plural(data.closeups, "крупный план", "крупных плана", "крупных планов")}`);
  if (data.missing > 0) parts.push(`${data.missing} без расчёта`);
  return parts.join(" · ");
}
